import type { IContent } from '@/@types/cms';
import { extractTextFromHTML, formatPrice } from '@/utils/helpers';

export type FeaturedBook = IContent & {
  displayName?: string;
  imageUrl?: string;
  variantThumbnailImage?: {
    url?: string;
  };
  code?: string;
  sourceCode?: string;
  keyCode?: string;
  bookFormat?: number;
  formatDescription?: string;
  description?: string;
  discountedPrice?: number;
  fullPrice?: number;
  isOpenAccess?: boolean;
  isPurchased?: boolean;
  plusAvailable?: boolean;
  hasPlusAccessFromProgram?: boolean;
  hasPlusAccessFromProgramOrMembership?: boolean;
  plusUrl?: string;
  readOnPlusUrl?: string;
};

export type FeaturedBookPrice = {
  price: string;
  strikePrice?: string;
};

export type FeaturedBookPrimaryCta = {
  href: string;
  label: 'Read now on PLI PLUS';
};

const SINGLE_TITLE_SUBSCRIPTION_BOOK_FORMATS = new Set([13, 14, 15]);
const SINGLE_TITLE_SUBSCRIPTION_CONTENT_TYPES = new Set([
  'AnswerBookSingleTitleSubscriptionVariation',
  'TreatiseSingleTitleSubscriptionVariation',
  'CourseHandbookSingleTitleSubscriptionVariation'
]);

const hasPlusAccess = (book: FeaturedBook): boolean => {
  return (
    book.hasPlusAccessFromProgram === true || book.hasPlusAccessFromProgramOrMembership === true
  );
};

const isSingleTitleSubscriptionBook = (book: FeaturedBook): boolean => {
  if (
    typeof book.bookFormat === 'number' &&
    SINGLE_TITLE_SUBSCRIPTION_BOOK_FORMATS.has(book.bookFormat)
  ) {
    return true;
  }

  return (
    book.contentType?.some(contentType =>
      SINGLE_TITLE_SUBSCRIPTION_CONTENT_TYPES.has(contentType)
    ) === true
  );
};

const canReadOnPlus = (book: FeaturedBook, isAuthenticated: boolean): boolean => {
  if (!isAuthenticated) return false;
  if (isSingleTitleSubscriptionBook(book)) return book.isPurchased === true;

  return book.plusAvailable === true && hasPlusAccess(book);
};

export const stripHtml = (value?: string): string | undefined => {
  if (!value) return undefined;

  return extractTextFromHTML(value).replace(/\s+/g, ' ').trim();
};

export const getBookPrice = (book: FeaturedBook): FeaturedBookPrice | undefined => {
  const hasDiscountedPrice = typeof book.discountedPrice === 'number';
  const hasFullPrice = typeof book.fullPrice === 'number';

  if (!hasDiscountedPrice && !hasFullPrice) return undefined;

  const currentPrice = hasDiscountedPrice ? book.discountedPrice! : book.fullPrice!;
  const hasStrikePrice =
    hasFullPrice && hasDiscountedPrice && book.fullPrice !== book.discountedPrice;

  return {
    price: formatPrice(currentPrice),
    strikePrice: hasStrikePrice ? formatPrice(book.fullPrice!) : undefined
  };
};

export const getPliUrl = (url?: string): string | undefined => {
  if (!url) return undefined;

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return undefined;
  if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) return trimmedUrl;

  try {
    const parsedUrl = new URL(trimmedUrl);
    const isPliHttpUrl =
      (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') &&
      (parsedUrl.hostname === 'pli.edu' || parsedUrl.hostname.endsWith('.pli.edu'));

    return isPliHttpUrl ? trimmedUrl : undefined;
  } catch {
    return undefined;
  }
};

export const getLearnMoreHref = (
  productHref?: string,
  readOnPlusUrl?: string,
  primaryHref?: string
): string | undefined => {
  const learnMoreHref = productHref ?? readOnPlusUrl;

  return learnMoreHref && learnMoreHref !== primaryHref ? learnMoreHref : undefined;
};

export const getPrimaryCta = (
  book: FeaturedBook,
  _productHref?: string,
  readOnPlusUrl?: string,
  isAuthenticated = false
): FeaturedBookPrimaryCta | undefined => {
  if (!canReadOnPlus(book, isAuthenticated)) return undefined;
  if (readOnPlusUrl) return { href: readOnPlusUrl, label: 'Read now on PLI PLUS' };

  return undefined;
};
